<template>
  <div id="rebates" class="align-start fill-height">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading rebates information..."
    />

    <v-row v-else class="g-container-large" no-gutters>
      <v-dialog
        v-if="rebateDialog"
        v-model="rebateDialog"
        max-width="750px"
        persistent
        @click:outside="resetEdition"
        @keydown.esc="resetEdition"
      >
        <i-edit-rebate
          :rebate="editableRebate"
          @cancel="resetEdition"
          @save="reload"
        />
      </v-dialog>

      <v-dialog
        v-if="confirmationDialog"
        v-model="confirmationDialog"
        width="400px"
        @click:outside="resetDeletion"
        @keydown.esc="resetDeletion"
      >
        <i-confirmation-dialog
          action-text="Delete"
          :submitting="deletingRebate || updatingRebate"
          :text="confirmationDialogText"
          @cancel="resetDeletion"
          @confirm="confirmDeletion"
        />
      </v-dialog>

      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template>
          <v-row>
            <v-col cols="12" md="3" lg="2">
              <v-btn
                large
                rounded
                class="i-btn-block-secondary g-full-width"
                @click="rebateDialog = true"
              >
                Add Rebate
              </v-btn>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/rebates"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'ACTIVE' }"
                @click="selectedStatus = 'ACTIVE'"
                >Active</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{
                  'i-btn-select-active': selectedStatus === 'UPCOMING'
                }"
                @click="selectedStatus = 'UPCOMING'"
                >Upcoming</v-btn
              >
            </v-col>
            <v-col cols="4" md="3" lg="2">
              <v-btn
                large
                class="i-btn-select g-full-width"
                :class="{ 'i-btn-select-active': selectedStatus === 'PAST' }"
                @click="selectedStatus = 'PAST'"
                >Past</v-btn
              >
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" lg="6">
              <i-local-filter
                :filterPlaceholder="'Search Rebates by Target Type or Target(s) or Geography'"
                @filter="localFilter"
              />
            </v-col>
          </v-row>
        </template>

        <template>
          <v-row>
            <v-col cols="12">
              <template v-if="!loading && filteredRebatesByStatus.length === 0">
                <i-empty-results hideButton :title="emptyFilteredResultsText" />
              </template>

              <template v-else>
                <template v-if="$vuetifyDisplay.smAndDown">
                  <div
                    class="mt-0 pa-0"
                    v-for="item in filteredRebatesByStatus"
                    :key="item.rebateId"
                  >
                    <v-card class="rounded-2 pa-4 mb-6">
                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[0].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <v-chip
                            v-if="item.targetBannerGroups.length"
                            small
                            color="accent"
                            ><strong>BANNER</strong></v-chip
                          >
                          <v-chip
                            v-else-if="item.targetMultiStoreOperators.length"
                            small
                            color="primary"
                            ><strong>MSO</strong></v-chip
                          >
                          <v-chip v-else small color="secondary"
                            ><strong>{{
                              isILR ? 'CUSTOMER' : 'RETAILER'
                            }}</strong></v-chip
                          >
                        </v-col>
                      </v-row>

                      <v-divider class="my-2"></v-divider>

                      <v-row no-gutters>
                        <v-col cols="6" class="py-1">
                          <span
                            ><strong>{{ headers[1].text }}</strong></span
                          >
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetBannerGroups.length">
                            <span
                              v-for="item in item.targetBannerGroups"
                              :key="item.bannerGroupId"
                            >
                              {{ item.bannerGroupName }}
                              <br />
                            </span>
                          </span>
                          <span
                            v-else-if="item.targetMultiStoreOperators.length"
                          >
                            <span
                              v-for="item in item.targetMultiStoreOperators"
                              :key="item.multiStoreOperatorId"
                            >
                              {{ item.multiStoreOperatorName }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetStores.length">
                            <span
                              v-for="item in item.targetStores"
                              :key="item.storeId"
                            >
                              {{ item.storeName }}
                              <br />
                            </span>
                          </span>
                          <span v-else> - </span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[2].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span v-if="item.targetStoreDeliveryAddressStates">
                            <span
                              v-for="item in item.targetStoreDeliveryAddressStates"
                              :key="item.state"
                            >
                              {{ item.state }}
                              <br />
                            </span>
                          </span>
                          <span v-else-if="item.targetGeography === 'STORE'">{{
                            isILR ? 'Customer' : 'Retailer'
                          }}</span>
                          <span v-else>{{
                            $filters.sentenceCase(item.targetGeography)
                          }}</span>
                        </v-col>

                        <v-col cols="6" class="py-1">
                          <span>{{ headers[3].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{ $filters.formatDate(item.startDate) }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[4].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <div
                            class="pa-1"
                            style="display: inline-block"
                            :class="{
                              'i-status-block-red': item.endsToday,
                              'g-border-radius-s': item.endsToday
                            }"
                          >
                            <span>{{ $filters.formatDate(item.endDate) }}</span>
                          </div>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[5].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{
                            $filters.percentage(item.rebatePercentage)
                          }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1">
                          <span>{{ headers[6].text }}</span>
                        </v-col>
                        <v-col cols="6" class="py-1 pl-4">
                          <span>{{ rebateType(item.type) }}</span>
                        </v-col>
                      </v-row>

                      <template v-if="item.status !== 'PAST'">
                        <v-divider class="mt-2 mb-4"></v-divider>

                        <v-row no-gutters>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-blue g-full-width"
                              @click="editRebate(item)"
                            >
                              <span>Edit Rebate</span>
                              <v-icon small class="ml-1"
                                >mdi-circle-edit-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                          <v-col cols="12" class="py-1">
                            <v-btn
                              class="i-btn-actions-red g-full-width"
                              :disabled="
                                item.endsToday || item.status === 'PAST'
                              "
                              @click="startDeletion(item)"
                            >
                              <span>Delete Rebate</span>
                              <v-icon small class="ml-1"
                                >mdi-delete-outline</v-icon
                              >
                            </v-btn>
                          </v-col>
                        </v-row>
                      </template>
                    </v-card>
                  </div>
                </template>

                <template v-else>
                  <v-data-table
                    class="pt-2 elevation-1 i-data-table"
                    item-key="rebateId"
                    :headers="headers"
                    :items="filteredRebatesByStatus"
                    :items-per-page="-1"
                    :loading="loading"
                    :no-data-text="emptyFilteredResultsText"
                    :no-results-text="emptyFilteredResultsText"
                  >
                    <template #[`item.targetType`]="{ item }">
                      <v-chip
                        v-if="item.targetBannerGroups.length"
                        small
                        color="accent"
                        >BANNER</v-chip
                      >
                      <v-chip
                        v-else-if="item.targetMultiStoreOperators.length"
                        small
                        color="primary"
                        >MSO</v-chip
                      >
                      <v-chip v-else small color="secondary">{{
                        isILR ? 'CUSTOMER' : 'RETAILER'
                      }}</v-chip>
                    </template>

                    <template #[`item.targetValue`]="{ item }">
                      <span v-if="item.targetBannerGroups.length">
                        <span
                          v-for="item in item.targetBannerGroups"
                          :key="item.bannerGroupId"
                        >
                          {{ item.bannerGroupName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetMultiStoreOperators.length">
                        <span
                          v-for="item in item.targetMultiStoreOperators"
                          :key="item.multiStoreOperatorId"
                        >
                          {{ item.multiStoreOperatorName }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetStores.length">
                        <span
                          v-for="item in item.targetStores"
                          :key="item.storeId"
                        >
                          {{ item.storeName }}
                          <br />
                        </span>
                      </span>
                      <span v-else> - </span>
                    </template>

                    <template #[`item.targetGeography`]="{ item }">
                      <span v-if="item.targetStoreDeliveryAddressStates">
                        <span
                          v-for="item in item.targetStoreDeliveryAddressStates"
                          :key="item.state"
                        >
                          {{ item.state }}
                          <br />
                        </span>
                      </span>
                      <span v-else-if="item.targetGeography === 'STORE'">{{
                        isILR ? 'Customer' : 'Retailer'
                      }}</span>
                      <span v-else>{{
                        $filters.sentenceCase(item.targetGeography)
                      }}</span>
                    </template>

                    <template #[`item.startDate`]="{ item }">
                      <span>{{ $filters.formatDate(item.startDate) }}</span>
                    </template>

                    <template #[`item.endDate`]="{ item }">
                      <div
                        class="pa-1"
                        :class="{
                          'i-status-block-red': item.endsToday,
                          'g-border-radius-s': item.endsToday
                        }"
                      >
                        <span>{{ $filters.formatDate(item.endDate) }}</span>
                      </div>
                    </template>

                    <template #[`item.rebatePercentage`]="{ item }">
                      <span class="p-rebate">
                        {{ $filters.percentage(item.rebatePercentage) }}
                      </span>
                    </template>

                    <template #[`item.type`]="{ item }">
                      <span>{{ rebateType(item.type) }}</span>
                    </template>

                    <template #[`item.actions`]="{ item }">
                      <v-btn
                        color="secondary"
                        :disabled="item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Edit',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="editRebate(item)"
                      >
                        <v-icon>mdi-circle-edit-outline</v-icon>
                      </v-btn>

                      <v-btn
                        color="error"
                        :disabled="item.endsToday || item.status === 'PAST'"
                        icon
                        v-tooltip="{
                          content: 'Delete',
                          visible: $vuetifyDisplay.mdAndDown ? false : undefined
                        }"
                        @click="startDeletion(item)"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </template>
              </template>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment-timezone'
import { mapActions, mapGetters, mapState } from 'vuex'
import { fetchRebates, deleteRebate, updateRebate } from '@/services/rebates'
import FiltersHelper from '@/modules/dataMaintenance/helper/FiltersHelper'
import iConfirmationDialog from '@/components/ConfirmationDialog'
import iEditRebate from '../components/EditRebate'
import iLocalFilter from '@/components/LocalFilter'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'

export default {
  name: 'rebates',

  components: {
    iEmptyResults,
    iInstructions,
    iConfirmationDialog,
    iEditRebate,
    iLocalFilter,
    iOverlay
  },

  data: () => ({
    confirmationDialog: false,
    confirmationDialogText: '',
    currentDate: moment().format('YYYY-MM-DD'),
    deletingRebate: false,
    editableRebate: {},
    filterText: '',
    headers: [
      {
        text: 'TARGET TYPE',
        value: 'targetType',
        width: 150,
        align: 'center',
        sortable: false
      },
      {
        text: 'TARGET(S)',
        value: 'targetValue',
        width: '20%',
        align: 'center',
        sortable: false
      },
      {
        text: 'GEOGRAPHY',
        value: 'targetGeography',
        width: 150,
        align: 'center'
      },
      { text: 'START DATE', value: 'startDate', align: 'center' },
      { text: 'END DATE', value: 'endDate', align: 'center' },
      { text: 'REBATE %', value: 'rebatePercentage', align: 'center' },
      { text: 'REBATE TYPE', value: 'type', align: 'center' },
      {
        text: 'ACTIONS',
        value: 'actions',
        width: '200px',
        align: 'center',
        sortable: false
      }
    ],
    loading: true,
    rebateDialog: false,
    rebates: [],
    selectedRebate: null,
    selectedStatus: 'ACTIVE',
    updatingRebate: false
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('tenant', ['featureSupplierDirectRebatesEnabled', 'isILR']),

    emptyFilteredResultsText() {
      return `No ${this.selectedStatus} Rebates found`
    },

    mappedRebates() {
      return this.rebates.map((rebate) => {
        return {
          ...rebate,
          endsToday: rebate.endDate === this.currentDate
        }
      })
    },

    rebatesByStatus() {
      return this.mappedRebates
        .filter((rebate) => rebate.status === this.selectedStatus)
        .sort((r1, r2) => {
          if (r1.status === 'ACTIVE')
            return r1.startDate > r2.startDate ? -1 : 1
          if (r1.status === 'UPCOMING')
            return r1.startDate < r2.startDate ? -1 : 1
          if (r1.status === 'PAST') return r1.endDate > r2.endDate ? -1 : 1
        })
    },

    filteredRebatesByStatus() {
      const searchTextLowerCase = this.filterText.toLowerCase()

      return this.rebatesByStatus.filter((rebate) => {
        return (
          rebate.targetGeography.toLowerCase().includes(searchTextLowerCase) ||
          rebate.targetStoreDeliveryAddressStates?.filter((s) =>
            s.state.toLowerCase().includes(searchTextLowerCase)
          ).length > 0 ||
          FiltersHelper.filterByTargetType(rebate, searchTextLowerCase) ||
          FiltersHelper.filterByTargets(rebate, searchTextLowerCase)
        )
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    localFilter(filterText) {
      if (typeof filterText !== 'string') filterText = ''
      this.filterText = filterText
    },

    rebateType(type) {
      let rebateType = ''
      switch (type) {
        case 'OFF_INVOICE':
          rebateType = 'OFF Invoice'
          break
        case 'MONTHLY':
          rebateType = 'Monthly'
          break
        default:
          rebateType = '-'
      }
      return rebateType
    },

    // ***
    // Functions related to handling actions
    // ***

    startDeletion(item) {
      this.selectedRebate = item
      this.confirmationDialogText =
        this.selectedRebate.status === 'ACTIVE'
          ? 'Active Rebate will end midnight today (End Date auto updates)'
          : 'Confirm deletion of this Rebate'
      this.confirmationDialog = true
    },

    confirmDeletion() {
      this.selectedRebate.status === 'ACTIVE'
        ? this.endRebate(this.selectedRebate)
        : this.deleteRebate(this.selectedRebate.id)
    },

    reload() {
      this.getRebates()
      this.resetEdition()
    },

    resetDeletion() {
      this.confirmationDialog = false
      this.confirmationDialogText = ''
      this.selectedRebate = null
    },

    resetEdition() {
      this.editableRebate = {}
      this.rebateDialog = false
    },

    // ***
    // Functions related to coupons data
    // ***

    editRebate(rebate = {}) {
      this.editableRebate = _.cloneDeep(rebate)
      this.rebateDialog = true
    },

    deleteRebate(rebateRuleId) {
      this.deletingRebate = true
      this.addNotification({
        color: 'secondary',
        text: 'Deleting your rebate...'
      })

      deleteRebate(this.selectedSupplierId, rebateRuleId)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Rebate successfully deleted!'
          })
          this.getRebates()
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.deletingRebate = false
          this.resetDeletion()
        })
    },

    endRebate(rebate) {
      this.updatingRebate = true
      this.addNotification({
        color: 'secondary',
        text: 'Ending Rebate...'
      })

      const payload = {
        endDate: this.currentDate,
        startDate: rebate.startDate,
        rebatePercentage: rebate.rebatePercentage,
        type: rebate.type,
        targetBannerGroupIds:
          rebate.targetBannerGroups?.map(
            (bannerGroup) => bannerGroup.bannerGroupId
          ) || [],
        targetMultiStoreOperatorIds:
          rebate.targetMultiStoreOperators?.map(
            (mso) => mso.multiStoreOperatorId
          ) || [],
        targetStoreIds:
          rebate.targetStores?.map((store) => store.storeId) || [],
        targetStoreDeliveryAddressStates:
          rebate.targetStoreDeliveryAddressStates?.map(
            (state) => state.state
          ) || []
      }

      updateRebate(this.selectedSupplierId, this.selectedRebate.id, payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Rebate end date has been updated to today!'
          })
        })
        .catch((err) => {
          console.log(err)
          this.addNotification({
            color: 'error',
            text: 'Rebate could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingRebate = false
          this.resetDeletion()
          this.reload()
        })
    },

    getRebates() {
      this.loading = true
      fetchRebates(this.selectedSupplierId)
        .then((res) => {
          this.rebates = res
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Rebates could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    // Note: this is a simple guard to ensure that ILRMarketplace users can't browse to this page directly.
    // It is already hidden on the DataMaintenance.vue landing page, so if changing the behaviour there you will need to change it here too!
    if (!this.featureSupplierDirectRebatesEnabled)
      this.$router.push({ name: 'dashboard' })

    this.getRebates()
  }
}
</script>

<style lang="scss" scoped>
#rebates {
  .p-rebate {
    font-size: 16px;
    font-weight: bold;
    color: $primary-color;
  }
}
</style>
